import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { Box, Typography, Paper, CircularProgress, IconButton, Button, Chip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import { HOUR_LABEL_INTERVAL, hourLabelOnly } from '../utils/calendarLabels';
import { windowEvents } from '../utils/schoolWindow';

const COURTS = [
  { id: 'court-1', title: 'Court 1' },
  { id: 'court-2', title: 'Court 2' },
  { id: 'court-3', title: 'Court 3' },
  { id: 'court-4', title: 'Court 4' },
  { id: 'court-5', title: 'Court 5' },
  { id: 'court-6', title: 'Court 6' },
];

const CLASS_COLORS = {
  'junior-practice':   '#1E3A5F',
  'junior-private':    '#8E44AD',
  'junior-drop-in':    '#27AE60',
  'junior-evaluation': '#E67E22',
  'adult-practice':    '#C0392B',
  'league':            '#00838F',
  'block-time':        '#7F8C8D',
};

const RESERVATION_COLOR = '#27AE60';

function formatSessionLabel(t) {
  const map = {
    'junior-practice':   'Junior Practice',
    'junior-private':    'Junior Private',
    'junior-drop-in':    'Junior Drop-in',
    'junior-evaluation': 'Junior Evaluation',
    'adult-practice':    'Adult Practice',
    'league':            'League',
    'block-time':        'Block Time',
  };
  return map[t] || t;
}

export default function PublicSchedule() {
  // Two slug-based modes:
  //   mode='event'    — /event/:slug — load that class, navigate calendar to its date, auto-open popup
  //   mode='calendar' — /calendar/:slug — apply the named filter shortcut
  //   default         — /calendar — show everything
  const { slug, eventSlug } = useParams();
  // Path-based detection — react-router uses different param names per route
  const isEventRoute = typeof eventSlug === 'string' && eventSlug.length > 0;
  const filterSlug = !isEventRoute && slug ? slug : null;
  const calendarRef = useRef(null);
  const [classes, setClasses] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [filterConfig, setFilterConfig] = useState(null); // active calendarFilters doc, if any
  const [loading, setLoading] = useState(true);
  const [calendarTitle, setCalendarTitle] = useState('');
  const [view, setView] = useState('resourceTimeGridDay');
  const [popup, setPopup] = useState(null); // { event, x, y } for desktop tooltip
  const [popupDialog, setPopupDialog] = useState(null); // event for mobile dialog
  const [schoolSettings, setSchoolSettings] = useState(null);
  useEffect(() => {
    getDoc(doc(db, 'settings', 'schoolSchedule'))
      .then(s => setSchoolSettings(s.exists() ? s.data() : null)).catch(() => {});
  }, []);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    const load = async () => {
      try {
        const [classSnap, resSnap] = await Promise.all([
          getDocs(collection(db, 'classes')),
          getDocs(collection(db, 'reservations')),
        ]);
        const allClasses = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setClasses(allClasses);
        setReservations(resSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // If we're on /calendar/:slug, load the filter config
        if (filterSlug) {
          try {
            const fSnap = await getDoc(doc(db, 'calendarFilters', filterSlug));
            if (fSnap.exists()) setFilterConfig(fSnap.data());
            else console.warn(`No calendarFilters doc found for slug: ${filterSlug}`);
          } catch (e) {
            console.error('Failed to load filter config:', e);
          }
        }
      } catch (e) {
        console.error('Failed to load schedule:', e);
      }
      setLoading(false);
    };
    load();
  }, [filterSlug]);

  // /event/:slug — once classes load, navigate calendar to that event's date and open popup
  useEffect(() => {
    if (!isEventRoute || classes.length === 0) return;
    const target = classes.find(c => c.slug === eventSlug);
    if (!target) return;
    // Navigate calendar to that date once it mounts
    const tryNavigate = () => {
      const api = calendarRef.current?.getApi();
      if (!api) { setTimeout(tryNavigate, 100); return; }
      api.gotoDate(target.date);
      // Open popup with full details
      const props = { ...target, eventType: 'class' };
      const titleStr = target.eventName || `${formatSessionLabel(target.sessionType)}${target.skillGroup ? ` — ${target.skillGroup}` : ''}`;
      setPopupDialog({ props, title: titleStr });
    };
    tryNavigate();
  }, [isEventRoute, eventSlug, classes]);

  // Scroll to earliest event of visible range when classes finish loading
  useEffect(() => {
    if (loading) return;
    if (classes.length === 0 && reservations.length === 0) return;
    const tryScroll = (attempts = 0) => {
      const api = calendarRef.current?.getApi();
      if (!api) {
        if (attempts < 20) setTimeout(() => tryScroll(attempts + 1), 100);
        return;
      }
      const view = api.view;
      const vt = view.type;
      if (!['resourceTimeGridDay', 'timeGridDay', 'timeGridWeek'].includes(vt)) return;
      const startMs = view.activeStart.getTime();
      const endMs = view.activeEnd.getTime();
      const allStarts = [];
      classes.forEach(c => {
        if (c.status === 'canceled') return;
        if (!c.date || !c.startTime) return;
        const d = new Date(`${c.date}T${c.startTime}`);
        if (d.getTime() >= startMs && d.getTime() < endMs) allStarts.push(d);
      });
      reservations.forEach(r => {
        if (r.type === 'tournament') return;
        if (!r.date || !r.startTime) return;
        const d = new Date(`${r.date}T${r.startTime}`);
        if (d.getTime() >= startMs && d.getTime() < endMs) allStarts.push(d);
      });
      if (allStarts.length === 0) return;
      const earliest = allStarts.reduce((a, b) => (a < b ? a : b));
      const hh = earliest.getHours();
      const mm = earliest.getMinutes();
      const totalMin = Math.max(0, hh * 60 + mm - 30);
      const padded = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}:00`;
      try { api.scrollToTime(padded); } catch (e) { /* noop */ }
    };
    setTimeout(() => tryScroll(), 200);
  }, [loading, classes, reservations, view]);

  // Apply the active filter (from /calendar/:slug) to classes.
  // filterConfig may have: { athleteType, skillGroup, sessionType, headCoachId } — any combination.
  function passesFilter(c) {
    if (!filterConfig) return true;
    const f = filterConfig.filters || filterConfig;
    if (f.athleteType) {
      const expectedPrefix = f.athleteType === 'junior' ? 'junior' : f.athleteType === 'adult' ? 'adult' : null;
      if (expectedPrefix && !(c.sessionType || '').startsWith(expectedPrefix)) return false;
    }
    if (f.skillGroup && c.skillGroup !== f.skillGroup) return false;
    if (f.sessionType && c.sessionType !== f.sessionType) return false;
    if (f.headCoachId && c.headCoachId !== f.headCoachId) return false;
    return true;
  }

  const calendarEvents = [
    ...classes
      .filter(c => c.status !== 'canceled')
      .filter(passesFilter)
      .map(c => {
        const color = c.eventColor || CLASS_COLORS[c.sessionType] || '#1E3A5F';
        const resourceId = c.courtNumber?.startsWith('court-') ? c.courtNumber : `court-${c.courtNumber}`;
        const courtNum = parseInt((c.courtNumber || '').replace('court-', '')) || 99;
        return {
          id: c.id,
          resourceId,
          courtOrder: courtNum,
          title: c.eventName || `${formatSessionLabel(c.sessionType)}${c.skillGroup ? ` — ${c.skillGroup}` : ''}`,
          start: `${c.date}T${c.startTime}`,
          end: `${c.date}T${c.endTime}`,
          backgroundColor: color,
          borderColor: color,
          extendedProps: { ...c, eventType: 'class' },
        };
      }),
    ...(filterConfig ? [] : reservations)
      .filter(r => r.type !== 'tournament')
      .flatMap(r => {
        const courts = Array.isArray(r.courts) ? r.courts : [r.courts].filter(Boolean);
        return courts.map(courtId => ({
          id: `res-${r.id}-${courtId}`,
          resourceId: courtId,
          title: r.title || r.type,
          start: `${r.date}T${r.startTime}`,
          end: `${r.date}T${r.endTime}`,
          backgroundColor: RESERVATION_COLOR,
          borderColor: RESERVATION_COLOR,
          extendedProps: { ...r, eventType: 'reservation' },
        }));
      }),
    // The high-school window, on the full calendar only (a filtered calendar
    // leaves out court reservations too).
    ...(filterConfig ? [] : windowEvents(schoolSettings, COURTS.map(c => c.id))),
  ];

  const handleEventMouseEnter = (info) => {
    if (window.innerWidth >= 768) {
      const rect = info.el.getBoundingClientRect();
      setPopup({ props: info.event.extendedProps, title: info.event.title, x: rect.left + rect.width / 2, y: rect.top });
    }
  };

  const handleEventMouseLeave = () => setPopup(null);

  const handleEventClick = (info) => {
    if (window.innerWidth < 768) {
      setPopupDialog({ props: info.event.extendedProps, title: info.event.title });
    }
  };

  const EventPopupContent = ({ props, title }) => {
    const fmtTime = (t) => {
      if (!t) return '';
      const [h, min] = t.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      return `${h % 12 || 12}:${String(min).padStart(2, '0')} ${ampm}`;
    };
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>{title}</Typography>
        {props.startTime && props.endTime && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
            🕐 {fmtTime(props.startTime)} – {fmtTime(props.endTime)}
          </Typography>
        )}
        {props.courtNumber && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
            🏐 Court {(props.courtNumber || '').replace('court-', '')}
          </Typography>
        )}
        {props.skillGroup && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
            📊 {props.skillGroup}
          </Typography>
        )}
        {props.maxAthletes && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            👥 Max {props.maxAthletes} athletes
          </Typography>
        )}
        {props.description && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 0.5 }}>
            {props.description}
          </Typography>
        )}
        {props.slug && (
          <Box sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.1)', pt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              🔗 /event/{props.slug}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const navigate = (action) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (action === 'prev') api.prev();
    else if (action === 'next') api.next();
    else if (action === 'today') api.today();
  };

  const VIEWS = [
    { key: 'resourceTimeGridDay', label: 'Day' },
    { key: 'timeGridWeek',        label: 'Week' },
    { key: 'dayGridMonth',        label: 'Month' },
  ];

  return (
    <Box sx={{ background: '#0D1E35', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <style>{`
        .fc .fc-event-main { color: white !important; }
        .fc .fc-event-title { color: white !important; font-weight: 600 !important; }
        .fc .fc-event-time { color: rgba(255,255,255,0.9) !important; }
        .fc .fc-daygrid-event { color: white !important; }
        .fc .fc-daygrid-event .fc-event-title { color: white !important; font-weight: 600 !important; }
        .fc .fc-daygrid-event .fc-event-time { color: rgba(255,255,255,0.9) !important; }
      `}</style>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
            {filterConfig?.name || 'Class Schedule'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
            {filterConfig
              ? `Filtered view · Torch Beach Volleyball · Geneva, FL`
              : `Torch Beach Volleyball · Geneva, FL`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {VIEWS.map(v => (
            <Button key={v.key} size="small" variant={view === v.key ? 'contained' : 'outlined'}
              onClick={() => { setView(v.key); calendarRef.current?.getApi().changeView(v.key); }}
              sx={{ borderColor: 'rgba(201,168,76,0.4)', color: view === v.key ? '#0D1E35' : '#C9A84C',
                backgroundColor: view === v.key ? '#C9A84C' : 'transparent',
                '&:hover': { backgroundColor: view === v.key ? '#e0be6a' : 'rgba(201,168,76,0.1)' } }}>
              {v.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Calendar nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('prev')} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#C9A84C' } }}>
          <ChevronLeftIcon />
        </IconButton>
        <IconButton onClick={() => navigate('today')} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#C9A84C' } }}>
          <TodayIcon />
        </IconButton>
        <IconButton onClick={() => navigate('next')} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#C9A84C' } }}>
          <ChevronRightIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, ml: 1 }}>{calendarTitle}</Typography>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {Object.entries(CLASS_COLORS).filter(([k]) => k !== 'block-time').map(([type, color]) => (
          <Chip key={type} label={formatSessionLabel(type)} size="small"
            sx={{ backgroundColor: color, color: 'white', fontSize: '0.7rem', fontWeight: 600 }} />
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress sx={{ color: '#C9A84C' }} />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[resourceTimeGridPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin, scrollGridPlugin]}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            initialView="resourceTimeGridDay"
            resources={COURTS}
            events={calendarEvents}
            selectable={false}
            editable={false}
            eventTextColor="white"
            eventDisplay="block"
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            eventClick={handleEventClick}
            eventDidMount={(info) => {
              // Prevent focus on event elements to avoid aria-hidden conflict
              info.el.setAttribute('tabindex', '-1');
              info.el.setAttribute('aria-hidden', 'true');
            }}
            eventOrder="courtOrder,start"
            headerToolbar={false}
            slotMinTime="07:30:00"
            slotMaxTime="22:00:00"
            slotDuration="00:15:00"
            slotLabelInterval={HOUR_LABEL_INTERVAL}
            slotLabelContent={hourLabelOnly}
            height="auto"
            contentHeight={680}
            nowIndicator={true}
            allDaySlot={false}
            resourceAreaWidth="90px"
            resourceAreaHeaderContent="Court"
            titleFormat={{ weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }}
            datesSet={(info) => {
              if (info.view.type === 'resourceTimeGridDay' || info.view.type === 'timeGridDay') {
                const d = info.start;
                setCalendarTitle(d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }));
              } else {
                setCalendarTitle(info.view.title);
              }
              // Scroll to the earliest event of the visible range (only for time-based views)
              if (info.view.type === 'resourceTimeGridDay' || info.view.type === 'timeGridDay' || info.view.type === 'timeGridWeek') {
                const startMs = info.start.getTime();
                const endMs = info.end.getTime();
                const eventsInRange = calendarEvents.filter(e => {
                  const t = new Date(e.start).getTime();
                  return t >= startMs && t < endMs;
                });
                console.log('[Schedule] datesSet scroll check', {
                  view: info.view.type,
                  rangeStart: info.start.toISOString(),
                  rangeEnd: info.end.toISOString(),
                  totalEvents: calendarEvents.length,
                  eventsInRange: eventsInRange.length,
                });
                if (eventsInRange.length > 0) {
                  const earliest = eventsInRange.reduce((a, b) => (new Date(a.start) < new Date(b.start) ? a : b));
                  // Use the local hour from the Date object — works whether `start` is a string or Date
                  const earliestDate = new Date(earliest.start);
                  const hh = earliestDate.getHours();
                  const mm = earliestDate.getMinutes();
                  // Pad back 30 min so the event isn't right at the top edge
                  const totalMin = Math.max(0, hh * 60 + mm - 30);
                  const padded = `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}:00`;
                  console.log('[Schedule] scrolling to', padded, 'for event at', earliestDate.toISOString());
                  // Defer — FullCalendar needs the DOM to settle, especially after view change
                  setTimeout(() => {
                    try {
                      const api = calendarRef.current?.getApi();
                      if (api) api.scrollToTime(padded);
                    } catch (e) { console.warn('scrollToTime failed:', e); }
                  }, 100);
                }
              }
            }}
            eventContent={(info) => {
              const props = info.event.extendedProps;
              return (
                <Box sx={{ px: 0.5, py: 0.25, overflow: 'hidden', height: '100%' }}>
                  <Typography sx={{ fontWeight: 700, color: 'white', display: 'block', lineHeight: 1.2, fontSize: '0.72rem' }}>
                    {info.event.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', display: 'block' }}>
                    {info.timeText}
                    {props.courtNumber && (
                      <> · Court {(props.courtNumber || '').replace('court-', '')}</>
                    )}
                  </Typography>
                </Box>
              );
            }}
          />
        </Paper>
      )}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
          © {new Date().getFullYear()} Torch Beach Volleyball · app.torchvolleyball.com
        </Typography>
      </Box>

      {/* Desktop hover tooltip */}
      {popup && (
        <Box sx={{
          position: 'fixed',
          left: Math.min(popup.x, window.innerWidth - 220),
          top: Math.max(popup.y - 8, 8),
          transform: 'translate(-50%, -100%)',
          zIndex: 9999,
          background: 'rgba(13,30,53,0.97)',
          border: '1px solid rgba(201,168,76,0.3)',
          borderRadius: 2,
          p: 1.5,
          minWidth: 200,
          maxWidth: 280,
          pointerEvents: 'none',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <EventPopupContent props={popup.props} title={popup.title} />
        </Box>
      )}

      {/* Mobile click dialog */}
      <Dialog open={!!popupDialog} onClose={() => setPopupDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0D1E35', color: 'white', fontWeight: 700, borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
          Class Details
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D1E35', pt: 2 }}>
          {popupDialog && <EventPopupContent props={popupDialog.props} title={popupDialog.title} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
